import { createReducer, on } from '@ngrx/store';
import { ProductStateEntry } from '../../api/types/enums';
import { TableColumnDelta } from '../../api/types/interfaces';
import { clearColumnWidthAtPath, extractDelta, setColumnWidthAtPath } from './table-columns.merge';
import * as TableColumnsActions from './table-columns.actions';

/**
 * NBO-2165: this slice persists ONLY the user's sparse column-layout intent (a delta).
 * The effective layout is derived in table-columns.selectors.ts via
 * mergeColumns(CODE_DEFAULTS, delta). Code-owned structural fields
 * (tenant sales-pricing visibility) live in table-columns.defaults.ts and are never
 * stored here.
 *
 * A missing leaf (undefined) means "no customization" → pure code defaults. Containers
 * are seeded so the per-key reducer handlers can spread into them safely.
 */
export interface ListClasses {
  [key: string]: TableColumnDelta | undefined;
}

export interface State {
  paymethodsTableColumns?: TableColumnDelta;
  carriersTableColumns?: TableColumnDelta;
  surchargesTableColumns?: TableColumnDelta;
  taxesTableColumns?: TableColumnDelta;
  backofficeUsersTableColumns?: TableColumnDelta;
  listing: { valueSetTableColumns?: TableColumnDelta; categoryChildrenTableColumns?: TableColumnDelta };
  picker: { valueSetTableColumns?: TableColumnDelta };
  valueSetItemTableColumns?: TableColumnDelta;
  orderlistsTableColumns?: TableColumnDelta;
  favoriteListing: { favoritesTableColumns?: TableColumnDelta };
  favoriteUserListing: { favoritesTableColumns?: TableColumnDelta };
  attributeDescriptionsTableColumns: ListClasses;
  orderStatusTableColumns?: TableColumnDelta;
  orderStatusSetTableColumns?: TableColumnDelta;
  userListing: { usersTableColumns: { contacts?: TableColumnDelta; companies?: TableColumnDelta; customers?: TableColumnDelta } };
  userPicking: { usersTableColumns: { contacts?: TableColumnDelta; companies?: TableColumnDelta; customers?: TableColumnDelta } };
  pricesheetListing: { priceSheetTableColumns?: TableColumnDelta };
  pricesheetUserListing: { priceSheetTableColumns?: TableColumnDelta };
  eventActionsTableColumns?: TableColumnDelta;
  loginTokensTableColumns?: TableColumnDelta;
  templatesListing: { templatesTableColumns?: TableColumnDelta };
  templatesPicker: { templatesTableColumns?: TableColumnDelta };
  templateErrorLogsListing: { templateErrorLogsTableColumns?: TableColumnDelta };
  agentsTableColumns?: TableColumnDelta;
  restApiKeyTableColumns?: TableColumnDelta;
  gqlApiKeyTableColumns?: TableColumnDelta;
  clusterDefinitionTableColumns?: TableColumnDelta;
  businessRulesTableColumns?: TableColumnDelta;
  channelsTableColumns?: TableColumnDelta;
  companiesTableColumns?: TableColumnDelta;
  warehousesTableColumns?: TableColumnDelta;
  productListing: { productTableColumns?: TableColumnDelta };
  productPicker: { productTableColumns?: TableColumnDelta };
  ordersListing: { productTableColumns?: TableColumnDelta; spOrdersTableColumns?: TableColumnDelta; boOrdersTableColumns?: TableColumnDelta };
  ordersPicker: { productTableColumns?: TableColumnDelta; spOrdersTableColumns?: TableColumnDelta; boOrdersTableColumns?: TableColumnDelta };
  quotesListing: { productTableColumns?: TableColumnDelta; ordersTableColumns?: TableColumnDelta };
  quotesPicker: { productTableColumns?: TableColumnDelta; ordersTableColumns?: TableColumnDelta };
  requests: { productTableColumns?: TableColumnDelta; ordersTableColumns?: TableColumnDelta };
}

export const initialState: State = {
  listing: {},
  picker: {},
  favoriteListing: {},
  favoriteUserListing: {},
  attributeDescriptionsTableColumns: {},
  userListing: { usersTableColumns: {} },
  userPicking: { usersTableColumns: {} },
  pricesheetListing: {},
  pricesheetUserListing: {},
  templatesListing: {},
  templatesPicker: {},
  templateErrorLogsListing: {},
  productListing: {},
  productPicker: {},
  ordersListing: {},
  ordersPicker: {},
  quotesListing: {},
  quotesPicker: {},
  requests: {}
};

const isContainer = (value: unknown): value is Record<string, unknown> =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  !['displayed', 'available', 'hidden'].some((field) => Array.isArray((value as Record<string, unknown>)[field]));

/** Lays a hydrated tree over the seeded containers, so the per-key handlers can keep spreading into them. */
export function seedTableColumnTree(seed: unknown, tree: unknown): unknown {
  if (!isContainer(seed) || !isContainer(tree)) return tree === undefined ? seed : tree;
  const out: Record<string, unknown> = { ...seed };
  for (const [key, value] of Object.entries(tree)) out[key] = seedTableColumnTree(seed[key], value);
  return out;
}

export const tableColumnsReducer = createReducer(
  initialState,

  // NBO-2516: the signed-in user's layouts from their account, over the seeded containers.
  on(TableColumnsActions.tableColumnsHydrated, (_state, { tree }) => seedTableColumnTree(initialState, tree) as State),

  // NBO-2408: path-addressed width write — patches the delta in place, never rebuilds it.
  on(TableColumnsActions.columnWidthChanged, (state, { segments, columnId, width }) =>
    setColumnWidthAtPath(state, segments, columnId, width)
  ),
  on(TableColumnsActions.columnWidthReset, (state, { segments, columnId }) =>
    clearColumnWidthAtPath(state, segments, columnId)
  ),

  on(TableColumnsActions.payMethodsTableColumns, (state, { payload }) => ({
    ...state,
    paymethodsTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.carriersTableColumns, (state, { payload }) => ({
    ...state,
    carriersTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.surchargesTableColumns, (state, { payload }) => ({
    ...state,
    surchargesTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.taxesTableColumns, (state, { payload }) => ({
    ...state,
    taxesTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.backofficeUsersTableColumns, (state, { payload }) => ({
    ...state,
    backofficeUsersTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.valueSetTableColumns, (state, { payload }) => ({
    ...state,
    [payload.stateEntry]: {
      ...state[payload.stateEntry],
      valueSetTableColumns: extractDelta(payload.columnsObj)
    }
  })),

  on(TableColumnsActions.valueSetItemTableColumns, (state, { payload }) => ({
    ...state,
    valueSetItemTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.orderlistsTableColumns, (state, { payload }) => ({
    ...state,
    orderlistsTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.favoritesTableColumns, (state, { payload }) => ({
    ...state,
    [payload.stateEntry]: {
      ...state[payload.stateEntry],
      favoritesTableColumns: extractDelta(payload.columnsObj)
    }
  })),

  on(TableColumnsActions.attributeDescriptionsTableColumns, (state, { payload }) => ({
    ...state,
    attributeDescriptionsTableColumns: {
      ...state.attributeDescriptionsTableColumns,
      [payload.attrClass]: extractDelta(payload.columnObj)
    }
  })),

  on(TableColumnsActions.orderStatusTableColumns, (state, { payload }) => ({
    ...state,
    orderStatusTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.orderStatusSetTableColumns, (state, { payload }) => ({
    ...state,
    orderStatusSetTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.usersTableColumns, (state, { payload }) => ({
    ...state,
    [payload.stateEntry]: {
      ...state[payload.stateEntry],
      usersTableColumns: {
        ...state[payload.stateEntry].usersTableColumns,
        [payload.tab]: extractDelta(payload.columnsObj)
      }
    }
  })),

  on(TableColumnsActions.priceSheetTableColumns, (state, { payload }) => ({
    ...state,
    [payload.stateEntry]: {
      ...state[payload.stateEntry],
      priceSheetTableColumns: extractDelta(payload.columnsObj)
    }
  })),

  on(TableColumnsActions.eventActionsTableColumns, (state, { payload }) => ({
    ...state,
    eventActionsTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.loginTokensTableColumns, (state, { payload }) => ({
    ...state,
    loginTokensTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.templatesTableColumns, (state, { payload }) => ({
    ...state,
    [payload.stateEntry]: {
      ...state[payload.stateEntry],
      templatesTableColumns: extractDelta(payload.columnsObj)
    }
  })),

  on(TableColumnsActions.templateErrorLogsTableColumns, (state, { payload }) => ({
    ...state,
    [payload.stateEntry]: {
      ...state[payload.stateEntry],
      // Was written under `templatesTableColumns` (a copy-paste), so an error-log column edit landed
      // in the wrong sub-key and was never read back (found in NBO-2505).
      templateErrorLogsTableColumns: extractDelta(payload.columnsObj)
    }
  })),

  on(TableColumnsActions.agentsTableColumns, (state, { payload }) => ({
    ...state,
    agentsTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.restApiKeyTableColumns, (state, { payload }) => ({
    ...state,
    restApiKeyTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.gqlApiKeyTableColumns, (state, { payload }) => ({
    ...state,
    gqlApiKeyTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.clusterDefinitionTableColumns, (state, { payload }) => ({
    ...state,
    clusterDefinitionTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.businessRulesTableColumns, (state, { payload }) => ({
    ...state,
    businessRulesTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.channelsTableColumns, (state, { payload }) => ({
    ...state,
    channelsTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.companiesTableColumns, (state, { payload }) => ({
    ...state,
    companiesTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.warehousesTableColumns, (state, { payload }) => ({
    ...state,
    warehousesTableColumns: extractDelta(payload)
  })),

  on(TableColumnsActions.categoryChildrenTableColumns, (state, { payload }) => ({
    ...state,
    listing: {
      ...state.listing,
      categoryChildrenTableColumns: extractDelta(payload),
    },
  })),

  on(TableColumnsActions.productListTableColumns, (state, { payload }) => {
    const key = payload.stateEntry === ProductStateEntry.productList ? 'productListing' : 'productPicker';
    return { ...state, [key]: { ...state[key], productTableColumns: extractDelta(payload.columns) } };
  }),

  on(TableColumnsActions.ordersProductTableColumns, (state, { payload }) => {
    const key = payload.stateEntry === 'listing' ? 'ordersListing' : 'ordersPicker';
    return { ...state, [key]: { ...state[key], productTableColumns: extractDelta(payload.columns) } };
  }),

  on(TableColumnsActions.ordersSpTableColumns, (state, { payload }) => {
    const key = payload.stateEntry === 'listing' ? 'ordersListing' : 'ordersPicker';
    return { ...state, [key]: { ...state[key], spOrdersTableColumns: extractDelta(payload.columns) } };
  }),

  on(TableColumnsActions.ordersBoTableColumns, (state, { payload }) => {
    const key = payload.stateEntry === 'listing' ? 'ordersListing' : 'ordersPicker';
    return { ...state, [key]: { ...state[key], boOrdersTableColumns: extractDelta(payload.columns) } };
  }),

  on(TableColumnsActions.quotesProductTableColumns, (state, { payload }) => {
    const key = payload.stateEntry === 'listing' ? 'quotesListing' : 'quotesPicker';
    return { ...state, [key]: { ...state[key], productTableColumns: extractDelta(payload.columns) } };
  }),

  on(TableColumnsActions.quotesTableColumns, (state, { payload }) => {
    const key = payload.stateEntry === 'listing' ? 'quotesListing' : 'quotesPicker';
    return { ...state, [key]: { ...state[key], ordersTableColumns: extractDelta(payload.columns) } };
  }),

  on(TableColumnsActions.requestsProductTableColumns, (state, { payload }) => ({
    ...state,
    requests: { ...state.requests, productTableColumns: extractDelta(payload) }
  })),

  on(TableColumnsActions.requestsTableColumns, (state, { payload }) => ({
    ...state,
    requests: { ...state.requests, ordersTableColumns: extractDelta(payload) }
  }))
);
